import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Lock } from "lucide-react";
import { api } from "../../lib/api";
import { StickyBack } from "../../components/StickyBack";
import { AVIS_PAGE } from "./hubAvisContent";

const DARK = { backgroundColor: "#2D181E", backgroundImage: "radial-gradient(circle at 100% 0%, rgba(123,17,58,0.55), transparent 60%)" };

function Line({ l }) {
  if (typeof l === "string") return <p className="text-sm font-light leading-relaxed text-ink-soft">{l}</p>;
  if (l.h) return <p className="pt-3 text-sm font-semibold text-bordeaux first:pt-0">{l.h}</p>;
  if (l.lead) return <p className="text-[15px] font-medium leading-relaxed text-ink">{l.lead}</p>;
  if (l.note) return <p className="mt-2 rounded-xl bg-bordeaux-tint/50 px-4 py-3 text-sm font-light leading-relaxed text-bordeaux-dark">{l.note}</p>;
  return (
    <p className="flex gap-2.5 text-sm font-light leading-relaxed text-ink-soft">
      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-bordeaux" />{l.q}
    </p>
  );
}

function Section({ s, i }) {
  return (
    <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }} data-testid={`avis-section-${i}`} className="space-y-4">
      <div className="rounded-[28px] p-6 text-white shadow-[0_16px_48px_rgba(45,24,30,0.22)] sm:p-8" style={DARK}>
        <h2 className="font-sans text-xl font-medium sm:text-2xl">{s.title}</h2>
        <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F3D2D9]">Consignes</p>
        <div className="mt-2 space-y-3">
          {s.consignes.map((p, k) => <p key={k} className="text-sm font-light leading-relaxed text-white/80">{p}</p>)}
        </div>
      </div>
      {s.asides.map((a, k) => (
        <div key={k} className="rounded-[24px] border border-line bg-white p-5 shadow-card sm:p-6">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-bordeaux">{a.title}</p>
          <div className="space-y-2">{a.lines.map((l, j) => <Line key={j} l={l} />)}</div>
        </div>
      ))}
    </motion.section>
  );
}

export function AvisContent() {
  return <div className="space-y-12">{AVIS_PAGE.sections.map((s, i) => <Section key={i} s={s} i={i} />)}</div>;
}

export default function HubAvis() {
  const navigate = useNavigate();
  const [locked, setLocked] = useState(null);
  useEffect(() => {
    api.get("/client/hub-pages").then(({ data }) => setLocked(!!data.avis_locked)).catch(() => setLocked(false));
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl space-y-8">
      <StickyBack onClick={() => navigate("/espace/hub")}>Revenir à The Elevate Hub</StickyBack>
      <div className="flex items-center gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-bordeaux text-white shadow-[0_8px_24px_rgba(123,17,58,0.35)]"><Star size={24} /></span>
        <div>
          <h1 className="font-sans text-3xl font-medium text-ink sm:text-4xl">{AVIS_PAGE.title}</h1>
          <p className="mt-1 text-sm font-light text-ink-muted">{AVIS_PAGE.subtitle}</p>
        </div>
      </div>
      {locked === true && (
        <div data-testid="avis-locked" className="flex items-center gap-3 rounded-2xl border border-line bg-white p-6 text-ink-muted"><Lock size={18} /> Cette ressource n'est pas encore disponible.</div>
      )}
      {locked === false && <AvisContent />}
    </motion.div>
  );
}
